import type { OrderOpenStatus } from "../sharedTypes/orders"
import api from "@/services/api"
import type { RecursivelyConvertDatesToStrings } from "@/utils/typeUtilities"
import {
  TRANSACTION_METHOD,
  type TransactionSignatureStatus,
  type TransactionStatus,
  type TransactionType,
} from "../sharedTypes/transactions"
import type { MaybeRef } from "@vueuse/core"
import { useQuery } from "@tanstack/vue-query"
import { BASE_ORDERS_QUERY_KEY } from "@/services/queryKeys"
import type {
  PaymentDetailCard,
  PaymentDetailCash,
  PaymentDetailSplit,
} from "../sharedTypes/invoices"

export type OrderDetailRefund = {
  id: string
  type: TransactionType
  createdAt: Date
  invoiceId: string
  parentTransactionId: string | null
  amountTransactedGross: number
  amountTransactedGratuity: number
  amountTransactedTotal: number
  currency: string
  transactionMethod: string
  uti: string | null
  status: TransactionStatus
  deviceSerialNumber: string | null
  isDcc: boolean
  isDccFieldsPopulated: boolean
  dccCardCurrencyAmount: number
  dccCardholderCurrency: string
}

type OrderDetailPaymentBase = {
  id: string
  type: TransactionType
  amountTransactedGross: number
  amountTransactedGratuity: number
  amountCashback: number
  amountTransactedTotal: number
  currency: string
  createdAt: Date
  invoiceId: string
  refunds: Array<OrderDetailRefund>
  status: TransactionStatus
  deviceSerialNumber: string | null
}

type OrderDetailPaymentCard = OrderDetailPaymentBase & {
  transactionMethod: (typeof TRANSACTION_METHOD)["POS_CARD"]
  cardScheme: string | null
  cardNumberLast4: string
  acquirerResponseCode: string | null
  amountCashback: number
  signatureStatus: TransactionSignatureStatus | null
  retrievalReferenceNumber: string | null
  uti: string | null
  isDcc: boolean
  isDccFieldsPopulated: boolean
  dccCardCurrencyAmount: number
  dccCardholderCurrency: string
}

type OrderDetailPaymentCash = OrderDetailPaymentBase & {
  transactionMethod: (typeof TRANSACTION_METHOD)["POS_CASH"]
  cashTendered: number
  changeGiven: number
}

type OrderDetailPaymentUnknown = OrderDetailPaymentBase & {
  transactionMethod: null
}

export type OrderDetailPayment =
  | OrderDetailPaymentCard
  | OrderDetailPaymentCash
  | OrderDetailPaymentUnknown

export type OrderDetailOrderItem = {
  id: string
  product: {
    id: string
    hasVariablePrice: boolean
    name: string
    color: string | null
  } | null
  productModifierLabels: Array<string> | null
  price: number
  note: string | null
}

export type OrderDetailInvoice = {
  id: string
  amountGross: number
  createdAt: Date
  isPaid: boolean
  paidAt: Date | null
  paidBy:
    | PaymentDetailCard
    | PaymentDetailCash
    | PaymentDetailSplit
    | null
  amountBalancedGross: number
  amountBalancedGratuity: number
  amountBalancedCashback: number
  amountBalancedTotal: number
}

export type RetrieveOrderResponse = {
  id: string
  note: string | null
  createdAt: Date
  totalCost: number
  cashierId: string
  invoice: OrderDetailInvoice | null
  orderItems: Array<OrderDetailOrderItem>
  openStatus: OrderOpenStatus
  payments: Array<OrderDetailPayment>
  orderNumber: number | null
  table: {
    id: string
    name: string
  } | null
  isCollection: boolean
  customerName: string | null
  customerPhone: string | null
  collectionAt: Date | null
  serviceChargeTotal: number | null
  serviceChargePercentage: number | null
  serviceChargeFlat: number | null
}

export const retrieveOrder = async (
  orderId: string
): Promise<RetrieveOrderResponse> => {
  const { body } = await api.get<
    RecursivelyConvertDatesToStrings<RetrieveOrderResponse>
  >(`/orders/${orderId}`)

  return {
    ...body,
    createdAt: DateFns.parseISO(body.createdAt),
    collectionAt: body.collectionAt
      ? DateFns.parseISO(body.collectionAt)
      : null,
    invoice: body.invoice && {
      ...body.invoice,
      createdAt: DateFns.parseISO(body.invoice.createdAt),
      paidAt: body.invoice.paidAt
        ? DateFns.parseISO(body.invoice.paidAt)
        : null,
    },
    payments: body.payments.map((payment) => ({
      ...payment,
      createdAt: DateFns.parseISO(payment.createdAt),
      refunds: payment.refunds.map((refund) => ({
        ...refund,
        createdAt: DateFns.parseISO(refund.createdAt),
      })),
    })),
  }
}

export const useOrder = (orderId: MaybeRef<string>) =>
  useQuery(
    [BASE_ORDERS_QUERY_KEY, orderId],
    ({ queryKey: [_name, orderId_] }) =>
      retrieveOrder(unref(orderId_)),
    { refetchInterval: 60 * 1000 }
  )
